import { useState, useEffect, useRef, useCallback } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ChevronLeft, ChevronRight, X, CheckCircle2, XCircle,
  RotateCcw, Loader2, Volume2, VolumeX, Mic, MicOff, Star
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSpeech } from "@/hooks/useSpeech";

interface Slide {
  title: string;
  emoji: string;
  color: string;
  bullet_points: string[];
  summary: string;
  quiz_question?: string;
  quiz_options?: string[];
  quiz_answer?: string;
  image_url?: string;
  image_prompt?: string;
}

interface Props {
  slides: Slide[];
  lessonTitle: string;
  onClose: () => void;
  onGenerateImage?: (slideIndex: number, prompt: string) => void;
  generatingImages?: Set<number>;
}

const colorMap: Record<string, { bg: string; border: string; text: string; accent: string; light: string }> = {
  blue: { bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-200 dark:border-blue-800", text: "text-blue-800 dark:text-blue-200", accent: "bg-blue-500", light: "bg-blue-100 dark:bg-blue-900/40" },
  green: { bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800", text: "text-emerald-800 dark:text-emerald-200", accent: "bg-emerald-500", light: "bg-emerald-100 dark:bg-emerald-900/40" },
  purple: { bg: "bg-purple-50 dark:bg-purple-950/30", border: "border-purple-200 dark:border-purple-800", text: "text-purple-800 dark:text-purple-200", accent: "bg-purple-500", light: "bg-purple-100 dark:bg-purple-900/40" },
  orange: { bg: "bg-orange-50 dark:bg-orange-950/30", border: "border-orange-200 dark:border-orange-800", text: "text-orange-800 dark:text-orange-200", accent: "bg-orange-500", light: "bg-orange-100 dark:bg-orange-900/40" },
  pink: { bg: "bg-pink-50 dark:bg-pink-950/30", border: "border-pink-200 dark:border-pink-800", text: "text-pink-800 dark:text-pink-200", accent: "bg-pink-500", light: "bg-pink-100 dark:bg-pink-900/40" },
  teal: { bg: "bg-teal-50 dark:bg-teal-950/30", border: "border-teal-200 dark:border-teal-800", text: "text-teal-800 dark:text-teal-200", accent: "bg-teal-500", light: "bg-teal-100 dark:bg-teal-900/40" },
  amber: { bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-800", text: "text-amber-800 dark:text-amber-200", accent: "bg-amber-500", light: "bg-amber-100 dark:bg-amber-900/40" },
  indigo: { bg: "bg-indigo-50 dark:bg-indigo-950/30", border: "border-indigo-200 dark:border-indigo-800", text: "text-indigo-800 dark:text-indigo-200", accent: "bg-indigo-500", light: "bg-indigo-100 dark:bg-indigo-900/40" },
};

const QUIZ_CARD_EMOJIS = ["🅰️", "🅱️", "🔵", "🟢"];


/** Extract clickable letters/keywords from a bullet point */
function parseInteractiveText(text: string) {
  // Match letters in parentheses like (ب), (أ) or repeated patterns like ب.. ب.. بطة
  const parts: { type: "text" | "letter" | "keyword"; value: string }[] = [];
  // Pattern: match (X) where X is 1-3 chars, or standalone Arabic letters before ..
  const regex = /\(([^\)]{1,4})\)|(\S+)\.\./g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    if (match[1]) {
      parts.push({ type: "letter", value: match[1] });
    } else if (match[2]) {
      parts.push({ type: "keyword", value: match[2] });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }
  return parts.length > 0 ? parts : [{ type: "text" as const, value: text }];
}

export default function LessonSlidesViewer({ slides, lessonTitle, onClose, onGenerateImage, generatingImages }: Props) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [quizAnswer, setQuizAnswer] = useState<string | null>(null);
  const [direction, setDirection] = useState(0);
  const [currentNarrationLine, setCurrentNarrationLine] = useState(-1);
  const [starsEarned, setStarsEarned] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [shakingOption, setShakingOption] = useState<number | null>(null);
  const [completedSlides, setCompletedSlides] = useState<Set<number>>(new Set());
  
  const narrationAbortRef = useRef(false);

  const {
    isSpeaking, speak, stopSpeaking, encourageCorrect, encourageWrong,
    isListening, startListening, stopListening, voiceResult, clearVoiceResult,
  } = useSpeech({
    lang: "ar-SA",
    onVoiceResult: (transcript) => {
      handleVoiceQuizMatch(transcript);
    },
  });

  const slide = slides[currentSlide];
  const colors = colorMap[slide?.color] || colorMap.blue;
  const isLastSlide = currentSlide === slides.length - 1;

  // Auto-narrate line-by-line when slide changes
  useEffect(() => {
    if (!slide) return;
    narrationAbortRef.current = false;
    setCurrentNarrationLine(-1);

    const lines = [slide.title, slide.summary, ...slide.bullet_points];
    let i = 0;

    const narrateNext = () => {
      if (narrationAbortRef.current || i >= lines.length) {
        setCurrentNarrationLine(-1);
        return;
      }
      // title=0, summary=1, bullets start at 2
      setCurrentNarrationLine(i);
      const synth = window.speechSynthesis;
      synth.cancel();
      const voices = synth.getVoices();
      const arVoice = voices.find((v) => v.lang.startsWith("ar") && v.name.toLowerCase().includes("google"))
        || voices.find((v) => v.lang.startsWith("ar"));
      const utt = new SpeechSynthesisUtterance(lines[i]);
      if (arVoice) { utt.voice = arVoice; utt.lang = arVoice.lang; } else { utt.lang = "ar-SA"; }
      utt.rate = 0.85;
      utt.pitch = 1.1;
      utt.onend = () => { i++; setTimeout(narrateNext, 300); };
      utt.onerror = () => { i++; setTimeout(narrateNext, 300); };
      synth.speak(utt);
    };

    // Small delay before starting
    const timeout = setTimeout(narrateNext, 600);
    return () => {
      narrationAbortRef.current = true;
      clearTimeout(timeout);
      window.speechSynthesis.cancel();
    };
  }, [currentSlide]); // eslint-disable-line

  // Voice quiz matching
  const handleVoiceQuizMatch = useCallback((transcript: string) => {
    if (!slide?.quiz_options || !slide?.quiz_answer || quizAnswer) return;
    const normalize = (s: string) => s.replace(/[^\u0600-\u06FF\s]/g, "").trim();
    const spoken = normalize(transcript);
    const match = slide.quiz_options.find(opt => {
      const n = normalize(opt);
      return spoken.includes(n) || n.includes(spoken);
    });
    if (match) {
      handleQuizAnswer(match);
    }
  }, [slide, quizAnswer]); // eslint-disable-line

  const handleQuizAnswer = useCallback((option: string) => {
    if (quizAnswer) return;
    const isCorrect = option === slide?.quiz_answer;
    setQuizAnswer(option);

    if (isCorrect) {
      setStarsEarned(s => s + 1);
      setShowCelebration(true);
      encourageCorrect();
      setCompletedSlides(prev => new Set(prev).add(currentSlide));
      setTimeout(() => setShowCelebration(false), 2500);
    } else {
      const idx = slide?.quiz_options?.indexOf(option) ?? -1;
      setShakingOption(idx);
      encourageWrong();
      // Allow retry after shake
      setTimeout(() => {
        setShakingOption(null);
        setQuizAnswer(null);
      }, 1200);
    }
  }, [quizAnswer, slide, currentSlide, encourageCorrect, encourageWrong]);

  const goNext = () => {
    if (currentSlide < slides.length - 1) {
      narrationAbortRef.current = true;
      stopSpeaking();
      setDirection(1);
      setCurrentSlide(p => p + 1);
      setQuizAnswer(null);
      clearVoiceResult();
      setCompletedSlides(prev => new Set(prev).add(currentSlide));
    }
  };

  const goPrev = () => {
    if (currentSlide > 0) {
      narrationAbortRef.current = true;
      stopSpeaking();
      setDirection(-1);
      setCurrentSlide(p => p - 1);
      setQuizAnswer(null);
      clearVoiceResult();
    }
  };

  const goToSlide = (i: number) => {
    narrationAbortRef.current = true;
    stopSpeaking();
    setDirection(i > currentSlide ? 1 : -1);
    setCurrentSlide(i);
    setQuizAnswer(null);
    clearVoiceResult();
  };

  if (!slide) return null;

  // Determine which line is being narrated:
  // 0 = title, 1 = summary, 2+ = bullet_points
  const isTitleNarrating = currentNarrationLine === 0;
  const isSummaryNarrating = currentNarrationLine === 1;
  const narratingBulletIndex = currentNarrationLine >= 2 ? currentNarrationLine - 2 : -1;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-background/80 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { narrationAbortRef.current = true; stopSpeaking(); onClose(); }}>
            <X className="w-5 h-5" />
          </Button>
          <h2 className="text-sm font-semibold truncate max-w-[200px] sm:max-w-none">{lessonTitle}</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Stars earned */}
          <div className="flex items-center gap-0.5">
            {Array.from({ length: slides.filter(s => s.quiz_question).length || 1 }).map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < starsEarned ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/30"}`} />
            ))}
          </div>
          {/* Slide progress as dots journey */}
          <div className="flex items-center gap-1 bg-muted/50 rounded-full px-2 py-1">
            <span className="text-xs">🚀</span>
            <span className="text-xs font-medium text-muted-foreground">
              {currentSlide + 1}/{slides.length}
            </span>
          </div>
        </div>
      </div>

      {/* Progress bar with gradient */}
      <div className="h-2 bg-muted/30 relative overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-l from-yellow-400 via-orange-400 to-pink-500 rounded-full"
          animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
        {/* Rocket on progress */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 text-sm"
          animate={{ right: `${((currentSlide + 1) / slides.length) * 100}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{ marginRight: "-8px" }}
        >
          🚀
        </motion.div>
      </div>

      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
          >
            {/* Flying confetti */}
            {Array.from({ length: 20 }).map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  opacity: 1,
                  x: 0,
                  y: 0,
                  scale: 0,
                }}
                animate={{
                  opacity: [1, 1, 0],
                  x: (Math.random() - 0.5) * 400,
                  y: (Math.random() - 0.5) * 400,
                  scale: [0, 1.5, 0.5],
                  rotate: Math.random() * 720,
                }}
                transition={{ duration: 1.5, delay: i * 0.05 }}
                className="absolute text-2xl"
                style={{ top: "50%", left: "50%" }}
              >
                {["⭐", "🌟", "✨", "🎉", "💫"][i % 5]}
              </motion.div>
            ))}
            {/* Central message */}
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: [0, 1.2, 1], rotate: [10, -5, 0] }}
              exit={{ scale: 0 }}
              className="bg-background/95 rounded-3xl p-8 text-center shadow-2xl max-w-xs mx-4 z-10 border-4 border-yellow-400"
            >
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 0.8 }} className="text-6xl mb-2">🏆</motion.div>
              <p className="text-2xl font-bold text-foreground mb-1">أحسنت يا بطل! 🎉</p>
              <p className="text-sm text-muted-foreground">إجابة صحيحة! استمر</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slide Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex items-start justify-center">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentSlide}
            custom={direction}
            initial={{ opacity: 0, x: direction > 0 ? 100 : -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction > 0 ? -100 : 100 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full max-w-2xl"
          >
            <Card className={`${colors.bg} ${colors.border} border-2 shadow-lg overflow-hidden`}>
              {/* Slide Header */}
              <div className={`${colors.accent} text-white p-4 sm:p-6`}>
                <div className="flex items-center justify-between">
                  <motion.div
                    className="flex items-center gap-3"
                    animate={isTitleNarrating ? { scale: [1, 1.03, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    <motion.span
                      className="text-3xl sm:text-4xl"
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                    >
                      {slide.emoji}
                    </motion.span>
                    <h3 className={`text-lg sm:text-xl font-bold transition-all ${isTitleNarrating ? "underline decoration-white/60 underline-offset-4" : ""}`}>
                      {slide.title}
                    </h3>
                    {isTitleNarrating && <Volume2 className="w-4 h-4 animate-pulse" />}
                  </motion.div>
                  <Button
                    variant="ghost" size="icon"
                    className="h-9 w-9 text-white/80 hover:text-white hover:bg-white/20"
                    onClick={() => {
                      if (isSpeaking) { stopSpeaking(); narrationAbortRef.current = true; setCurrentNarrationLine(-1); }
                      else speak(slide.title + ". " + slide.summary);
                    }}
                  >
                    {isSpeaking ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                </div>
              </div>

              <CardContent className="p-4 sm:p-6 space-y-4">
                {/* Interactive Image */}
                {slide.image_url ? (
                  <motion.div
                    className="rounded-xl overflow-hidden shadow-md cursor-pointer relative group"
                    whileTap={{ scale: 0.98 }}
                    onClick={() => speak(slide.title)}
                  >
                    <img src={slide.image_url} alt={slide.title} className="w-full h-48 sm:h-56 object-cover" />
                    {/* Tap indicator */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors"
                      initial={{ opacity: 1 }}
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ repeat: 3, duration: 1.5, delay: 1 }}
                    >
                      <span className="text-3xl opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg">🔊</span>
                    </motion.div>
                  </motion.div>
                ) : slide.image_prompt && onGenerateImage ? (
                  <div
                    className={`${colors.light} rounded-xl p-6 text-center cursor-pointer hover:opacity-80 transition-opacity`}
                    onClick={() => { if (!generatingImages?.has(currentSlide)) onGenerateImage(currentSlide, slide.image_prompt!); }}
                  >
                    {generatingImages?.has(currentSlide) ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">جاري توليد الصورة...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-4xl">🎨</span>
                        <p className={`text-sm font-medium ${colors.text}`}>اضغط لتوليد صورة توضيحية</p>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Summary with narration highlight */}
                <motion.p
                  className={`text-base sm:text-lg leading-relaxed ${colors.text} font-medium rounded-lg p-2 transition-all ${
                    isSummaryNarrating ? "bg-yellow-100/60 dark:bg-yellow-900/30 ring-2 ring-yellow-400/50" : ""
                  }`}
                  animate={isSummaryNarrating ? { scale: [1, 1.01, 1] } : {}}
                  transition={{ repeat: Infinity, duration: 1.2 }}
                >
                  {isSummaryNarrating && <Volume2 className="w-3.5 h-3.5 inline-block ml-1 animate-pulse text-yellow-600" />}
                  {slide.summary}
                </motion.p>

                {/* Interactive Bullet Points */}
                <div className="space-y-2">
                  {slide.bullet_points.map((point, i) => {
                    const isNarrating = narratingBulletIndex === i;
                    const parts = parseInteractiveText(point);
                    const hasInteractive = parts.some(p => p.type !== "text");

                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className={`rounded-xl p-3 text-sm sm:text-base ${colors.text} flex items-start gap-2 transition-all cursor-pointer ${
                          isNarrating
                            ? "bg-yellow-100/70 dark:bg-yellow-900/30 ring-2 ring-yellow-400/50 shadow-md scale-[1.02]"
                            : `${colors.light} hover:shadow-sm`
                        }`}
                        onClick={() => speak(point)}
                      >
                        <span className="shrink-0 mt-0.5 text-lg">{isNarrating ? "🔊" : "•"}</span>
                        <span className="flex flex-wrap items-center gap-1">
                          {hasInteractive ? parts.map((part, pi) => {
                            if (part.type === "letter" || part.type === "keyword") {
                              return (
                                <motion.button
                                  key={pi}
                                  whileHover={{ scale: 1.15 }}
                                  whileTap={{ scale: 1.3, rotate: [0, -5, 5, 0] }}
                                  onClick={(e) => { e.stopPropagation(); speak(part.value); }}
                                  className={`inline-flex items-center justify-center ${colors.accent} text-white font-bold text-lg sm:text-xl px-3 py-1 rounded-full shadow-md cursor-pointer hover:shadow-lg transition-shadow min-w-[2.5rem]`}
                                >
                                  {part.value}
                                </motion.button>
                              );
                            }
                            return <span key={pi}>{part.value}</span>;
                          }) : point}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Pronunciation Practice Button */}
                {slide.title && (
                  <div className="flex justify-center pt-2">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant={isListening ? "destructive" : "outline"}
                        size="sm"
                        className={`gap-2 rounded-full px-5 ${isListening ? "animate-pulse" : ""}`}
                        onClick={() => {
                          if (isListening) { stopListening(); }
                          else {
                            startListening();
                          }
                        }}
                      >
                        {isListening ? (
                          <><MicOff className="w-4 h-4" />جاري الاستماع...</>
                        ) : (
                          <><Mic className="w-4 h-4" />🎤 قُل الحرف</>
                        )}
                      </Button>
                    </motion.div>
                  </div>
                )}

                {/* Voice result feedback */}
                {voiceResult && !slide.quiz_question && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center p-3 rounded-xl bg-muted/50"
                  >
                    <p className="text-xs text-muted-foreground mb-1">سمعتك تقول:</p>
                    <p className="text-lg font-bold">"{voiceResult}"</p>
                    {voiceResult.includes(slide.title.charAt(0)) || voiceResult.includes(slide.title) ? (
                      <motion.p initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-green-600 font-bold mt-1">✅ ممتاز! نطق صحيح</motion.p>
                    ) : (
                      <p className="text-yellow-600 text-sm mt-1">حاول مرة ثانية 😊</p>
                    )}
                  </motion.div>
                )}

                {/* Quiz as Mini-Game */}
                {slide.quiz_question && slide.quiz_options && (
                  <div className="mt-4 border-t-2 border-dashed pt-4 space-y-4">
                    <motion.div
                      className="flex items-center gap-2 justify-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <motion.span
                        className="text-3xl"
                        animate={{ rotate: [0, 15, -15, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                      >
                        🤔
                      </motion.span>
                      <p className={`font-bold text-base sm:text-lg ${colors.text}`}>
                        {slide.quiz_question}
                      </p>
                    </motion.div>

                    {/* Voice answer */}
                    <div className="flex justify-center">
                      <Button
                        variant={isListening ? "destructive" : "outline"}
                        size="sm"
                        className={`gap-2 rounded-full px-5 ${isListening ? "animate-pulse" : ""}`}
                        onClick={() => isListening ? stopListening() : startListening()}
                        disabled={!!quizAnswer && quizAnswer === slide.quiz_answer}
                      >
                        {isListening ? (
                          <><MicOff className="w-4 h-4" />إيقاف التسجيل...</>
                        ) : (
                          <><Mic className="w-4 h-4" />🎤 جاوب بالصوت</>
                        )}
                      </Button>
                    </div>

                    {voiceResult && !quizAnswer && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`${colors.light} rounded-lg p-3 text-sm ${colors.text} text-center`}
                      >
                        <span className="text-xs text-muted-foreground block mb-1">سمعتك:</span>
                        <span className="font-medium">"{voiceResult}"</span>
                      </motion.div>
                    )}

                    {/* Game-style option cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {slide.quiz_options.map((option, i) => {
                        const isSelected = quizAnswer === option;
                        const isCorrect = option === slide.quiz_answer;
                        const showCorrect = quizAnswer && isCorrect && quizAnswer === slide.quiz_answer;
                        const showWrong = isSelected && !isCorrect;
                        const isShaking = shakingOption === i;

                        return (
                          <motion.button
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              x: isShaking ? [0, -8, 8, -6, 6, -3, 3, 0] : 0,
                              scale: showCorrect ? [1, 1.08, 1] : 1,
                            }}
                            transition={{
                              delay: i * 0.1,
                              x: { duration: 0.5 },
                              scale: { repeat: showCorrect ? 2 : 0, duration: 0.4 },
                            }}
                            whileHover={!quizAnswer ? { scale: 1.05, y: -3 } : {}}
                            whileTap={!quizAnswer ? { scale: 0.95 } : {}}
                            onClick={() => handleQuizAnswer(option)}
                            disabled={!!quizAnswer && quizAnswer === slide.quiz_answer}
                            className={`p-4 rounded-2xl text-base font-bold transition-all border-2 text-right flex items-center gap-3 ${
                              showCorrect
                                ? "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 border-green-400 shadow-lg shadow-green-200/50"
                                : showWrong
                                ? "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-red-400"
                                : `${colors.light} ${colors.text} ${colors.border} hover:shadow-lg`
                            }`}
                          >
                            <span className="text-2xl shrink-0">{QUIZ_CARD_EMOJIS[i] || "🔵"}</span>
                            <span className="flex-1">{option}</span>
                            {showCorrect && <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />}
                            {showWrong && <XCircle className="w-6 h-6 text-red-500 shrink-0" />}
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Footer */}
      <div className="border-t bg-background/80 backdrop-blur-sm p-3 space-y-2">
        {/* Star dots journey */}
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          {slides.map((_, i) => (
            <motion.button
              key={i}
              onClick={() => goToSlide(i)}
              whileHover={{ scale: 1.3 }}
              className={`w-3 h-3 rounded-full transition-all ${
                i === currentSlide
                  ? `${colorMap[slides[i].color]?.accent || "bg-primary"} scale-125 ring-2 ring-primary/30`
                  : completedSlides.has(i)
                  ? "bg-yellow-400"
                  : "bg-muted-foreground/20"
              }`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between max-w-2xl mx-auto w-full">
          <Button variant="outline" onClick={goPrev} disabled={currentSlide === 0} className="gap-1">
            <ChevronRight className="w-4 h-4" />
            السابق
          </Button>

          {isLastSlide ? (
            <Button
              onClick={() => { narrationAbortRef.current = true; stopSpeaking(); onClose(); }}
              className="gap-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-4 h-4" />
              إنهاء الشرح 🎉
            </Button>
          ) : (
            <Button onClick={goNext} className="gap-1">
              التالي
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
